import React, { useEffect, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import Map from "../MapWhenReady/MapWhenReady";
import RmfDialog, { DialogTitle } from "../Dialog/RmfDialog";
import { StatusChip, statusOf } from "../RestaurantList/RestaurantListCard";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS, VISUALLY_HIDDEN } from "../../utils/designTokens";
import { PickupFacts, PickupOnlyChip, pickupButtonSx } from "./PickupCard";
import { cuisineLine } from "./pickupText";

const pointOfRestaurant = (r) => {
  const c = r?.location?.coordinates;
  const lng = Number(c?.[0]);
  const lat = Number(c?.[1]);
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng) ? { lat, lng } : null;
};

function YouPin() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pointerEvents: "none" }}>
      <Box sx={{ px: 1, py: 0.25, borderRadius: RADIUS.pill, bgcolor: C.ink, color: "#FFFFFF", fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, lineHeight: "18px", boxShadow: "0 2px 6px rgba(26,25,24,0.3)" }}>
        You
      </Box>
      <Box sx={{ width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: `6px solid ${C.ink}` }} />
    </Box>
  );
}

/**
 * "See map": the customer and every pickup restaurant in one view. A pin is a
 * button with the restaurant's name; choosing one shows its card below the
 * map with "Order for pickup".
 */
function PickupMapDialog({ open, onClose, origin, restaurants, driveMinutes, onOrder }) {
  const phone = useMediaQuery("(max-width:599.95px)");
  const placed = restaurants.map((r) => ({ r, at: pointOfRestaurant(r) })).filter((p) => p.at);
  const [selectedId, setSelectedId] = useState(null);
  const selected = placed.find((p) => String(p.r._id) === String(selectedId))?.r || placed[0]?.r || null;
  const you = origin?.latitude != null && origin?.longitude != null ? { lat: Number(origin.latitude), lng: Number(origin.longitude) } : null;

  useEffect(() => {
    if (open) setSelectedId(null);
  }, [open]);

  // Every pin in view from the first frame (the map mounts on each open).
  // Set up front rather than on load, which waits for the map style. The side
  // padding leaves room for a name pill centred on its point.
  const pts = [you, ...placed.map((p) => p.at)].filter(Boolean);
  const lngs = pts.map((p) => p.lng);
  const lats = pts.map((p) => p.lat);
  const initialView =
    pts.length > 1
      ? {
          bounds: [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          fitBoundsOptions: { padding: { top: 56, bottom: 32, left: phone ? 72 : 100, right: phone ? 72 : 100 }, maxZoom: 14 },
        }
      : { longitude: pts[0]?.lng ?? 0, latitude: pts[0]?.lat ?? 0, zoom: 13 };

  return (
    <RmfDialog open={open} onClose={onClose} maxWidth={920} paperSx={{ p: 0, overflow: "hidden" }}>
      <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: { xs: 2.5, sm: 3 }, pb: 2 }}>
        <DialogTitle>Pickup near you</DialogTitle>
      </Box>
      <Box sx={{ position: "relative", height: { xs: "48vh", sm: "56vh" }, minHeight: 280, bgcolor: C.surface2 }}>
        {open && (
          <Map initialViewState={initialView} style={{ height: "100%", width: "100%" }} mapStyle="mapbox://styles/mapbox/streets-v12">
            <NavigationControl position="bottom-right" showCompass={false} />
            {you && (
              <Marker longitude={you.lng} latitude={you.lat} anchor="bottom" style={{ zIndex: 3 }}>
                <YouPin />
              </Marker>
            )}
            {placed.map(({ r, at }) => {
              const active = selected && String(selected._id) === String(r._id);
              return (
                <Marker key={r._id} longitude={at.lng} latitude={at.lat} anchor="bottom" style={{ zIndex: active ? 2 : 1 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSelectedId(r._id)}
                    aria-pressed={Boolean(active)}
                    sx={{
                      ...BUTTON_RESET,
                      ...FOCUS_RING,
                      maxWidth: phone ? 136 : 180,
                      px: phone ? 1 : 1.25,
                      py: 0.5,
                      borderRadius: RADIUS.pill,
                      bgcolor: active ? C.red : C.bg,
                      color: active ? "#FFFFFF" : C.ink,
                      border: `2px solid ${C.red}`,
                      fontFamily: FONT_BODY,
                      fontSize: phone ? 12 : 13,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      boxShadow: "0 2px 6px rgba(26,25,24,0.25)",
                    }}
                  >
                    {r.name}
                  </Box>
                </Marker>
              );
            })}
          </Map>
        )}
      </Box>
      {selected && (
        <Box sx={{ p: { xs: 2.5, sm: 3 }, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, gap: 2 }} aria-live="polite">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
              <PickupOnlyChip />
              <StatusChip status={statusOf(selected)} />
            </Box>
            <Box component="h3" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {selected.name}
            </Box>
            {cuisineLine(selected) && (
              <Box sx={{ fontFamily: FONT_BODY, fontSize: 14, color: C.muted, mt: 0.25 }}>{cuisineLine(selected)}</Box>
            )}
            <Box sx={{ mt: 0.75 }}>
              <PickupFacts r={selected} driveMinutes={driveMinutes(selected)} />
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => onOrder(selected)}
            sx={{ ...pickupButtonSx, width: { xs: "100%", sm: "auto" }, px: 3, flexShrink: 0 }}
          >
            Order for pickup
            <Box component="span" sx={VISUALLY_HIDDEN}>{` from ${selected.name}`}</Box>
          </Box>
        </Box>
      )}
    </RmfDialog>
  );
}

export default PickupMapDialog;
